import { ValueFormatterParams } from 'ag-grid-enterprise'

import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent } from '@fortem-monorepo/react-spa'
import { Flows } from '@fortem-monorepo/web-sdk'

import { Section } from '@/pages/funds/flows/components/Section'
import {
  baseFormatter,
  percentFormatter,
  to2dpNoRounding,
} from '@/pages/funds/flows/components/formatters'
import { StreamStageProps } from '@/pages/funds/flows/components/types'

export interface LongShortStageProps extends StreamStageProps<Flows.GetLongShortOutput> {}

export const LongShortStage: React.FC<LongShortStageProps> = ({ output }) => {
  const { rows, ...rest } = output

  return (
    <div className={'space-y-4'}>
      <Section title="Summary" defaultOpen={true}>
        <Widget exposed>
          <WidgetContent>
            <CustomAgGrid
              rowData={rows}
              columnDefs={longShortColDefs()}
              statusBar={{ statusPanels: [] }}
            />
          </WidgetContent>
        </Widget>
      </Section>
      <Section title="Long / Short legs" defaultOpen={true}>
        <Widget exposed>
          <WidgetContent>
            <CustomAgGrid
              rowData={[rest]}
              columnDefs={actionColDefs(output)}
              statusBar={{ statusPanels: [] }}
            />
          </WidgetContent>
        </Widget>
      </Section>
      <Section title="Prices" defaultOpen={true}>
        <Widget exposed>
          <WidgetContent>
            <CustomAgGrid
              // @ts-ignore
              rowData={(output.prices ?? []).sort((b, a) => {
                try {
                  return new Date(a.date).getTime() - new Date(b.date).getTime()
                } catch (e) {
                  console.error(e)
                }
              })}
              columnDefs={pricesColDefs(output.prices)}
            />
          </WidgetContent>
        </Widget>
      </Section>
    </div>
  )
}

const pricesColDefs = (prices: Flows.GetLongShortOutput['prices']) => {
  const unique_identifiers = [...new Set(prices.map((x) => Object.keys(x)).flat())].filter(
    (x) => x !== 'date',
  )

  return createColDefs<Flows.GetLongShortOutput['prices'][number]>([
    {
      field: 'date',
      headerName: 'Date',
    },
    ...unique_identifiers.map((i) => ({
      field: i as keyof (typeof prices)[number],
    })),
  ])
}

const actionColDefs = (data: Flows.GetLongShortOutput) => {
  return createColDefs<Omit<Flows.GetLongShortOutput, 'rows'>>([
    {
      field: 'net_flow',
      filter: false,
      width: 150,
      headerName: data.net_flow >= 0 ? 'Subscriptions' : 'Redemptions',
      valueFormatter: to2dpNoRounding,
    },
    {
      headerName: 'Current FX',
      filter: false,
      width: 110,
      valueGetter: (params) => params.data?.fx_usd_gbp.rate,
    },
    {
      headerName: 'FX Date',
      filter: false,
      width: 100,
      valueGetter: (params) => params.data?.fx_usd_gbp.date,
    },
    {
      hide: data.net_flow < 0,
      field: 'long_leg_upsize',
      width: 140,
      filter: false,
      headerName: 'Long Leg Upsize',
      valueFormatter: to2dpNoRounding,
    },
    {
      hide: data.net_flow < 0,
      field: 'short_leg_upsize',
      width: 140,
      filter: false,
      headerName: 'Short Leg Upsize',
      valueFormatter: to2dpNoRounding,
    },
    {
      hide: data.net_flow >= 0,
      field: 'long_leg_downsize',
      width: 140,
      filter: false,
      headerName: 'Long Leg Downsize',
      valueFormatter: to2dpNoRounding,
    },
    {
      hide: data.net_flow >= 0,
      field: 'short_leg_downsize',
      width: 140,
      filter: false,
      headerName: 'Short Leg Downsize',
      valueFormatter: to2dpNoRounding,
    },
    {
      field: 'us_bucket_final',
      headerName: `FX to ${data.net_flow >= 0 ? 'USD' : 'GBP'}`,
      valueFormatter: percentFormatter,
      filter: false,
    },
  ])
}

const longShortColDefs = () => {
  return createColDefs<Flows.LongShortRow>([
    {
      field: 'label',
      headerName: '',
      filter: false,
      sortable: false,
      valueFormatter: (params) => {
        const val = params.value
        try {
          return toISO8601(new Date(val))
        } catch (e) {
          return val
        }
      },
    },
    {
      field: 'investment_base_gbp',
      filter: false,
      headerName: 'Investment Base (£)',
    },
    {
      field: 'fx',
      headerName: 'FX',
      width: 120,
      cellStyle: { textAlign: 'center' },
      filter: false,
      valueFormatter: (params: ValueFormatterParams) => {
        // if (typeof params.value === "number") {
        //   return to2dpNoRounding(params)
        // }
        return params.value
      },
    },
    {
      field: 'long',
      headerName: 'Long',
      width: 80,
      filter: false,
      valueFormatter: (params) => {
        if (params.data?.label === 'Market Moves') {
          return percentFormatter(params)
        }
        return baseFormatter(params)
      },
    },
    {
      field: 'short',
      headerName: 'Short',
      width: 80,
      filter: false,
      valueFormatter: (params) => {
        if (params.data?.label === 'Market Moves') {
          return percentFormatter(params)
        }
        return baseFormatter(params)
      },
    },
    {
      field: 'long_pct',
      headerName: 'Long (%)',
      width: 90,
      filter: false,
      valueFormatter: percentFormatter,
    },
    {
      field: 'short_pct',
      headerName: 'Short (%)',
      width: 90,
      filter: false,
      valueFormatter: percentFormatter,
    },
    {
      field: 'long_short_usd',
      headerName: 'L/S ($)',
      width: 80,
      filter: false,
      valueFormatter: baseFormatter,
    },
    {
      field: 'long_short_gbp',
      headerName: 'L/S (£)',
      width: 80,
      filter: false,
      valueFormatter: baseFormatter,
    },
    {
      field: 'cash',
      headerName: 'Cash',
      width: 80,
      filter: false,
      valueFormatter: (params) => {
        if (params.data?.label === 'Market Moves') {
          return percentFormatter(params)
        }
        return baseFormatter(params)
      },
    },
    {
      field: 'investment_gbp',
      headerName: 'Investment (£)',
      width: 110,
      filter: false,
      valueFormatter: baseFormatter,
    },
    {
      field: 'us_bucket',
      headerName: 'US Bucket',
      width: 100,
      filter: false,
      valueFormatter: percentFormatter,
    },
    {
      field: 'uk_bucket',
      headerName: 'UK Bucket',
      width: 100,
      filter: false,
      valueFormatter: percentFormatter,
    },
  ])
}
